import { type MutableRefObject, type RefObject, useEffect, useRef, useState } from 'react'

function parseRootMarginPx(margin: string): { top: number; right: number; bottom: number; left: number } {
  const parts = margin.trim().split(/\s+/).map((p) => parseFloat(p) || 0)
  if (parts.length === 1) {
    return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] }
  }
  if (parts.length === 2) {
    return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] }
  }
  if (parts.length === 4) {
    return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] }
  }
  return { top: 0, right: 0, bottom: 0, left: 0 }
}

export type UseDeferUntilVisibleOptions = {
  /** 嵌套滚动容器；不传则用视口作为 root（等价于原生 lazy 的根） */
  rootRef?: RefObject<Element | null>
  /**
   * 直接传入滚动根 DOM（优先于 rootRef?.current）。
   * 用于横向 strip：state 升降级会触发 observer 重建。
   */
  rootEl?: Element | null
  /**
   * 递增时强制重建 IntersectionObserver（如图库 scroll port 通过 callback ref 晚挂载）。
   */
  rootAttachSignal?: number
  /** true：进入交集后再挂载真实 src */
  defer: boolean
  rootMargin?: string
}

/**
 * 用于嵌套 overflow 滚动：原生 img loading="lazy" 仅以视口为准，在 scroll-region 内不够可靠。
 */
export function useDeferUntilVisible({
  rootRef,
  rootEl,
  rootAttachSignal = 0,
  defer,
  rootMargin = '180px',
}: UseDeferUntilVisibleOptions): {
  wrapRef: MutableRefObject<HTMLDivElement | null>
  /** 是否已对观测 root 相交（或未启用 defer） */
  active: boolean
} {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(!defer)

  useEffect(() => {
    if (!defer) {
      setActive(true)
      return
    }
    setActive(false)
    const target = wrapRef.current
    if (!target) return

    const root = (rootEl !== undefined ? rootEl : rootRef?.current) ?? null
    const rootNode = root instanceof Element ? root : null
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setActive(true)
      },
      {
        root: rootNode,
        rootMargin,
        threshold: 0.01,
      },
    )
    io.observe(target)
    // 部分 WebKit 在嵌套 scroll-region 内 observe 后不派发初始相交，补一次同步判断
    queueMicrotask(() => {
      if (!wrapRef.current) return
      const records = io.takeRecords()
      if (records.some((e) => e.isIntersecting)) {
        setActive(true)
        return
      }
      const tr = target.getBoundingClientRect()
      if (tr.width <= 0 || tr.height <= 0) return
      const viewport = {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
      }
      const rootRect = rootNode?.getBoundingClientRect() ?? viewport
      const margin = parseRootMarginPx(rootMargin)
      const hit =
        tr.bottom >= rootRect.top - margin.top &&
        tr.top <= rootRect.bottom + margin.bottom &&
        tr.right >= rootRect.left - margin.left &&
        tr.left <= rootRect.right + margin.right
      if (hit) setActive(true)
    })
    return () => io.disconnect()
  }, [defer, rootMargin, rootEl, rootRef, rootAttachSignal])

  return { wrapRef, active }
}
